"use client"

import { CLOSE_BUTTON_CLASS, CLOSE_CORNER_CLASS } from "./close-button"
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react";

import { cn } from "@/lib/utils"
import { useAutoFocus, usePhone } from "@/hooks/use-media-query"
import { useTranslation } from "@/hooks/useTranslation"
import { pushSample, releaseVelocity, sheetReleasePassed, type DragSample } from "./sheet-gesture"
import { dialogActionRow } from "./action-row"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close


const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // The one scrim every overlay uses — see --scrim in index.css.
      // A keyframe, not a transition: the overlay MOUNTS with data-state=open,
      // so a transition from opacity-0 has no earlier frame to start from and
      // the scrim snapped to 55% black on the first frame while the sheet was
      // still at the bottom of the screen (measured in WebKit: opacity 1 at
      // +120ms, the sheet at 874px). It fades in over the first part of the
      // sheet's travel and out with the sheet's exit.
      "fixed inset-0 z-overlay bg-scrim/55",
      "data-[state=open]:overlay-fade-in data-[state=closed]:overlay-fade-out",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * How wide the centred card is allowed to get, from `sm` up.
 *
 * WHY A PROP AND NOT A CLASS AT EVERY CALL SITE
 * Every dialog used to state its own width, so the app had eleven different
 * ideas of "wide" and a dozen dialogs that stated nothing and inherited a
 * 512px card they visibly did not fit in. A rung is a decision a reviewer can
 * check; an arbitrary width is not.
 *
 * Each rung pairs the cap with `w-[min(95vw,…)]` below, so the card is always
 * the SMALLER of the rung and the screen. `full` is for surfaces that are
 * really a workspace rather than a form (the contact record, the unit matrix).
 *
 * `lg` is the default because it is what the previous hardcoded `sm:max-w-lg`
 * resolved to — every existing call site keeps exactly the width it had.
 */
const DIALOG_SIZES = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
  "2xl": "sm:max-w-4xl",
  "3xl": "sm:max-w-6xl",
  full: "sm:max-w-[min(95vw,110rem)]",
} as const

export type DialogSize = keyof typeof DIALOG_SIZES

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  size?: DialogSize
  /** Suppress the built-in close button (a header that provides its own). */
  hideClose?: boolean
  /**
   * Force the centred-card shape even on a phone. Only for a dialog that is
   * genuinely a small confirmation and would look wrong as a full-width sheet.
   */
  forceCard?: boolean
  /**
   * On a phone, open at the CONTENT's height instead of expanded, and only
   * grow if the content actually reaches the cap.
   *
   * Sheets open expanded (Andrew, 2026-09-15) because a phone is short of
   * height and a record wants all of it. A confirmation or a menu is the
   * opposite case: three rows and two buttons, and expanding them turned a
   * short panel into a full screen of empty surface with the rows stranded
   * at the top. PropPay only lets a sheet grow when it is sitting at its cap;
   * a `fit` sheet follows that rule, every other sheet keeps the decision.
   */
  fit?: boolean
  /**
   * On a phone, a MENU sheet (the tab bar's More) — pass it with `fit`: a sheet that never
   * grows to full height, and whose cap always leaves a thumb's height of scrim
   * above it (Andrew, 2026-09-28: "never take the menu drawer to the top of
   * the viewport, always show at least a thumb worth of space above it to
   * dismiss the menu by clicking off of it"). A long menu scrolls inside.
   */
  menu?: boolean
  /**
   * While true the panel cannot be dismissed AT ALL: Escape, the scrim, the ✕
   * and a downward drag of the handle are all inert. For the moment an action
   * is running (ConfirmDialog while its `confirm` promise is pending) — a
   * dismissal then would hide the outcome of something that is still
   * happening, and on a phone a flick is easy to make by accident.
   */
  locked?: boolean
}

/**
 * The first control a desktop dialog may land on when it opens.
 *
 * Radix focuses the first TABBABLE descendant, and `DialogBody` is tabbable
 * (tabIndex 0, so a keyboard can scroll it — axe `scrollable-region-focusable`).
 * It is also first in every dialog that has one, so every such dialog opened
 * with focus on its scroll region rather than its first field — and a focused
 * scroll region paints its ring round the whole body. That was the ring round
 * the Privacy and Terms dialogs on open (A30), and the reason a form dialog
 * did not land in its first field on a desk as the comment below intends.
 *
 * So: the first tabbable that is NOT a scroll region; if that is a field a
 * person types into, land there; otherwise land on the panel itself, which
 * draws no ring (index.css `[role=dialog]:focus-visible`) and keeps the trap
 * closed — the reader's first Tab then reaches the first control.
 */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable=""], [contenteditable="true"]'
const TYPED_FIELD =
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="color"]), textarea, select, [contenteditable=""], [contenteditable="true"], [role="combobox"]'

function firstTypedField(panel: HTMLElement): HTMLElement | null {
  for (const el of Array.from(panel.querySelectorAll<HTMLElement>(TABBABLE))) {
    if (el.closest("[aria-hidden='true']")) continue
    if (el.hasAttribute("data-dialog-body") || el.hasAttribute("data-sheet-grab")) continue
    // Not rendered (display:none, a collapsed branch): not a candidate.
    if (el.getClientRects().length === 0) continue
    return el.matches(TYPED_FIELD) ? el : null
  }
  return null
}

/**
 * ONE COMPONENT, TWO SHAPES.
 *
 * On a phone a modal is a SHEET: it rises from the bottom edge, keeps its top
 * corners round and its bottom square against the screen, and clears the home
 * indicator. That is the gesture iOS has taught everyone to expect from a panel
 * that covers the page. A centred card floating in the middle of a 402pt screen
 * — scrim on all four sides, 12pt of margin — reads as a web page imitating an
 * app, and it also wastes the only dimension a phone is short of.
 *
 * From `sm` up it is the centred card it always was: at that width there is room
 * around it, and a sheet would throw away the horizontal space.
 *
 * THE SHEET HAS TWO HEIGHTS. Collapsed is its content's height, capped at
 * 92dvh; expanded pins its top edge just under the status bar. It OPENS
 * expanded (Andrew, 2026-09-15: "dialogs on mobile should open as large as they
 * can") — a phone is short of height, not width, and a sheet that opened at its
 * content's height left the record's feed a sliver to scroll in. A sheet that
 * only ever went DOWN made the handle a half-control — it could dismiss a
 * panel and could not do the one thing a grabber on a bottom sheet mostly gets
 * used for, which is to see more of what is in it. The contact record, a long
 * notes list, a task with a description: the content is taller than the sheet
 * and the reader wants the sheet bigger, not gone.
 *
 * Expansion happens DURING the drag rather than on release: the panel is
 * anchored at the bottom, so any upward travel of the surface itself would
 * open a gap under it. Growing on the way up means the sheet answers the
 * gesture immediately and the bottom edge never leaves the screen. Down out of
 * full height returns to the content-sized sheet; down again from there
 * dismisses — two pulls to leave, so a reader who expanded a sheet to read it
 * does not lose it in one flick.
 *
 * The shape is resolved in JS via `usePhone()` rather than with responsive
 * utilities, because the two differ by more than a class list. `usePhone`
 * reads `matchMedia` DURING render, so the first paint is already the right
 * shape.
 *
 * On a phone, tapping the scrim dismisses (decision D1). On a desk it does
 * not: a dialog there closes only from its ✕, Cancel or Escape (Andrew,
 * 2026-10-05). `AlertDialog` never dismisses from outside, by design.
 */
/**
 * The control a dialog should hand focus back to when it closes.
 *
 * Radix returns focus to whatever was focused when the dialog opened. Safari
 * (iOS and macOS) does not focus a button when it is clicked or tapped, so for
 * a dialog opened from an onClick rather than a <DialogTrigger> that was
 * <body>, and a VoiceOver user closing the contact modal, the phone More
 * sheet or Create Task was dropped at the top of the document. Remember the
 * last control pressed, and fall back to it.
 */
let lastPressed: HTMLElement | null = null
if (typeof document !== "undefined") {
  document.addEventListener(
    "pointerdown",
    (e) => {
      const target = e.target instanceof Element ? e.target : null
      lastPressed = (target?.closest("button, a[href], [role='button'], [role='menuitem'], [role='tab']") as HTMLElement | null) ?? null
    },
    { capture: true, passive: true },
  )
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({
  className,
  children,
  size = "lg",
  hideClose,
  forceCard,
  fit = false,
  menu = false,
  locked = false,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onInteractOutside,
  style,
  ...props
}, ref) => {
  const t = useTranslation()
  const phone = usePhone() && !forceCard
  const mayAutoFocus = useAutoFocus()
  const panel = React.useRef<HTMLDivElement | null>(null)
  const dismiss = React.useRef<HTMLButtonElement | null>(null)
  // Opens expanded unless the caller asked for a `fit` sheet — see `fit`.
  const [expanded, setExpanded] = React.useState(!fit)

  // The control that opened it — captured as the dialog OPENS (in
  // handleOpenAutoFocus), not as this component renders: callers render
  // this component while the dialog is closed too.
  const returnTo = React.useRef<HTMLElement | null>(null)
  const handleCloseAutoFocus = (e: Event) => {
    onCloseAutoFocus?.(e)
    if (e.defaultPrevented) return
    const target = returnTo.current
    if (target && target.isConnected) {
      e.preventDefault()
      target.focus({ preventScroll: true })
    }
  }

  /**
   * Whether there is anything to expand INTO. Every sheet may grow unless it
   * is a `fit` sheet, which may grow only once its content has reached the
   * collapsed cap (92% of the visible height) — PropPay's `canExpand`.
   */
  const mayExpand = () => {
    if (menu) return false
    if (!fit) return true
    const el = panel.current
    return !!el && el.offsetHeight >= window.innerHeight * 0.92 - 2
  }

  /**
   * The grab handle drags the sheet, and it is a real button.
   *
   * The handle is drawn because it makes the sheet legible as a bottom sheet —
   * so it has to actually work. A handle that says "grab me" and ignores the
   * gesture is worse than no handle: the reader tries what the shape promised
   * and reads the result as a broken control.
   *
   * `aria-expanded` is the state and tapping toggles it, so the height a
   * pointer gets by dragging is the same height a keyboard or switch gets by
   * pressing — the drag is an enhancement on a control that works without it.
   * A short drag that ends with the pointer still over the handle fires a
   * click on the way out, which would toggle the height a second time and
   * undo what the drag just did; `dragged` is what stops that.
   *
   * Plain pointer events rather than framer-motion: framer is loaded through a
   * dynamic feature bundle here (see lib/motionFeatures.ts) and pulling its
   * drag feature into every dialog would put the whole animation engine back
   * on the static path. `animationName: "none"` in the inline style is the
   * important part of the downward follow — a running CSS animation outranks
   * an inline `transform`, so without cancelling it the panel would not
   * follow the finger at all.
   */
  const [dragY, setDragY] = React.useState(0)
  const dragFrom = React.useRef<number | null>(null)
  const dragged = React.useRef(false)
  // Recent pointer positions, for the release speed (sheet-gesture.ts).
  const samples = React.useRef<DragSample[]>([])

  const onHandleDown = (e: React.PointerEvent) => {
    dragFrom.current = e.clientY
    dragged.current = false
    samples.current = [{ t: e.timeStamp, y: e.clientY }]
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onHandleMove = (e: React.PointerEvent) => {
    if (dragFrom.current === null) return
    pushSample(samples.current, e.timeStamp, e.clientY)
    const dy = e.clientY - dragFrom.current
    if (Math.abs(dy) > 4) dragged.current = true
    // Upward, and deliberately at a much smaller threshold than the release
    // one: this is the sheet answering the pull, not deciding anything.
    if (!expanded && dy < -24 && mayExpand()) {
      setExpanded(true)
      dragFrom.current = e.clientY
      samples.current = [{ t: e.timeStamp, y: e.clientY }]
      setDragY(0)
      return
    }
    setDragY(Math.max(0, dy))
  }
  const onHandleUp = (e: React.PointerEvent) => {
    if (dragFrom.current === null) return
    const offset = Math.max(0, e.clientY - dragFrom.current)
    dragFrom.current = null
    pushSample(samples.current, e.timeStamp, e.clientY)
    // Far enough, OR a hard flick — see sheet-gesture.ts. Distance alone let
    // a short, fast flick spring back as if nothing had been asked.
    if (sheetReleasePassed(offset, releaseVelocity(samples.current, e.timeStamp))) {
      // Down out of full height returns to the content-sized sheet; down
      // again dismisses — unless the panel is locked, when it springs back.
      if (expanded) setExpanded(false)
      else if (!locked) dismiss.current?.click()
    }
    samples.current = []
    setDragY(0)
  }
  // A cancelled gesture (the OS took it) decides nothing.
  const onHandleCancel = () => {
    dragFrom.current = null
    samples.current = []
    setDragY(0)
  }
  const onHandleClick = () => {
    if (dragged.current) return
    // A menu has one height, so its handle's only job is to put it away.
    if (menu) {
      if (!locked) dismiss.current?.click()
      return
    }
    if (expanded || mayExpand()) setExpanded((v) => !v)
  }

  /**
   * On a touch device, open WITHOUT diving into the first field.
   *
   * Radix focuses the first focusable descendant on mount, which is usually an
   * input — and on a phone that raises the keyboard instantly, over the sheet
   * that just animated in. Focus still moves INTO the panel, just onto the
   * panel itself rather than a field: that is what keeps the focus trap closed
   * and lets a screen reader announce the dialog.
   *
   * Keyed on pointer, not width: an iPad at 1024px raises the same keyboard.
   * Under a pointer it lands in the first FIELD, or on the panel when the
   * first control is not one — never on the scroll region (see
   * `firstTypedField`). A caller's own handler runs first and wins if it
   * calls preventDefault.
   */
  const handleOpenAutoFocus = (e: Event) => {
    const focused = document.activeElement as HTMLElement | null
    returnTo.current = focused && focused !== document.body ? focused : lastPressed
    onOpenAutoFocus?.(e)
    if (e.defaultPrevented) return
    e.preventDefault()
    const el = panel.current
    if (!el) return
    const field = mayAutoFocus ? firstTypedField(el) : null
    ;(field ?? el).focus({ preventScroll: true })
  }

  /* `locked` swallows every dismissal Radix can start on its own. The caller's
     handler still runs first, so a form guarding unsaved input keeps working. */
  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    onEscapeKeyDown?.(e)
    if (locked) e.preventDefault()
    /* Escape with a combobox's list open closes the LIST, not the dialog.
       Radix listens for Escape on the document in the capture phase, so it
       hears the key before the field does and a stopPropagation there is too
       late: Escape to dismiss a project search (ui/chip-picker) threw away the
       whole half-filled Add user form. Cancelling only the dismissal leaves
       the field's own handler to close its list; the next Escape closes the
       dialog. PropPay LX, 2026-09-28. Not a cmdk input: that one says
       aria-expanded="true" always, so the rule would leave it no way out. */
    const target = e.target as HTMLElement | null
    if (
      target?.getAttribute("role") === "combobox" &&
      target.getAttribute("aria-expanded") === "true" &&
      !target.hasAttribute("cmdk-input")
    )
      e.preventDefault()
  }
  /* On a desk a dialog closes only from its own controls — the ✕, Cancel,
     Escape — never from a click on the page behind it (Andrew, 2026-10-05):
     a stray click beside a half-filled form threw the form away. A phone
     drawer keeps tap-the-scrim to dismiss (decision D1), and so does a menu or
     a panel with no ✕, which would otherwise have no way out. */
  const outsideClosesIt = phone || menu || !!hideClose
  const handlePointerDownOutside: typeof onPointerDownOutside = (e) => {
    onPointerDownOutside?.(e)
    if (locked || !outsideClosesIt) e.preventDefault()
  }
  const handleInteractOutside: typeof onInteractOutside = (e) => {
    onInteractOutside?.(e)
    if (locked || !outsideClosesIt) e.preventDefault()
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={(node) => {
          panel.current = node
          if (typeof ref === "function") ref(node)
          else if (ref) (ref as React.MutableRefObject<typeof node>).current = node
        }}
        tabIndex={-1}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        onEscapeKeyDown={handleEscapeKeyDown}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        aria-busy={locked || undefined}
        className={cn(
          "fixed z-modal flex flex-col p-0 overflow-clip surface-elevated",
          // A menu sheet wears `.menu-panel`, whose own ink must win; a
          // utility here would beat that component rule.
          !menu && "text-card-foreground",
          phone
            ? cn(
                // Sheet. `dvh` and not `vh`: on iOS `100vh` is the toolbar-free
                // height, so a 92vh sheet is taller than the window holding it
                // and its footer sits under the browser chrome.
                // bottom is the keyboard's top edge while one is up — see
                // index.css §THE VISUAL VIEWPORT. 0 otherwise.
                "border-x-0 border-b-0",
                "rounded-t-xl rounded-b-none",
                // Clears the home indicator. --sa-bottom, never a raw env() —
                // see index.css §LAYOUT TOKENS for why the insets have exactly
                // one entry point.
                "pb-[var(--sa-bottom)]",
                "data-[state=open]:dialog-sheet-in data-[state=closed]:dialog-sheet-out"
              )
            : cn(
                // Centred card. The width is the smaller of 95vw and the size
                // rung, so it adapts to the display without a second rule.
                // Centred in the VISIBLE viewport, not the layout one: with a
                // keyboard up on an iPad the two differ by the keyboard's
                // height, and a card centred in the layout viewport is half
                // hidden and then panned into view by iOS.
                "left-[50%] top-[calc(var(--vv-top,0px)_+_var(--vv-h,100dvh)_/_2)] translate-x-[-50%] translate-y-[-50%]",
                "w-[min(95vw,calc(100%-1.5rem))]",
                DIALOG_SIZES[size],
                "rounded-xl",
                // max-h subtracts the notch and home-indicator insets as well as
                // 1rem of breathing room, so a tall dialog on an installed PWA
                // stops short of the hardware rather than hiding its footer
                // buttons behind it.
                "max-h-[calc(var(--vv-h,100dvh)_-_2rem_-_var(--sa-top)_-_var(--sa-bottom))]",
                "data-[state=open]:dialog-spring-in data-[state=closed]:dialog-spring-out",
                SHORT_CARD_CONTENT,
                TABBED_CARD_HEIGHT
              ),
          className
        )}
        /*
          The sheet's GEOMETRY is inline, so no class can override it.

          Callers size their centred card with `h-[95dvh]`, `max-h-[80dvh]`,
          `max-w-sm` and the like, and on a phone those used to win. An expanded
          sheet then had its top pinned, its bottom pinned AND a height or a
          cap: a fixed box over-constrained that way ignores `bottom`, so it
          stopped short and left the page showing beneath it — the contact
          record ended 67pt above the screen edge (reported 2026-09-15).

          Appending override classes after the caller's was tried first and is
          not enough: tailwind-merge does not treat `max-h-[80dvh]` (or a
          `calc()` cap) as conflicting with `max-h-none`, both survive, and in
          the generated CSS the arbitrary value comes later and wins. An inline
          style beats every utility.

          Expanded, the height is whatever lies between the two pins;
          collapsed, it is the content's, capped at 92%. The 0.5rem floor on
          the top leaves a sliver of scrim on a device with no inset, so tapping
          outside still works at full height.
        */
        style={{
          ...style,
          ...(phone
            ? {
                left: 0,
                right: 0,
                width: "100%",
                maxWidth: "none",
                height: "auto",
                bottom: "var(--vv-bottom, 0px)",
                ...(expanded
                  ? { top: "calc(var(--vv-top, 0px) + max(0.5rem, var(--sa-top)))", maxHeight: "none" }
                  : menu
                    // 4.5rem of scrim above, under any top inset: a thumb's
                    // worth to tap, at every height of phone.
                    ? { top: "auto", maxHeight: "calc(var(--vv-h, 100dvh) - var(--sa-top) - 4.5rem)" }
                    : { top: "auto", maxHeight: "calc(var(--vv-h, 100dvh) * 0.92)" }),
              }
            : null),
          ...(dragY ? { transform: `translate3d(0, ${dragY}px, 0)`, animationName: "none", transition: "none" } : null),
        }}
        // Read by DialogBody and DialogFooter's short-viewport rules (see
        // SHORT_CARD_CONTENT): they only change shape inside a centred card.
        data-dialog-shape={phone ? "sheet" : "card"}
        {...props}
      >
        {phone && (
          <>
            <button
              type="button"
              data-sheet-grab=""
              onPointerDown={onHandleDown}
              onPointerMove={onHandleMove}
              onPointerUp={onHandleUp}
              onPointerCancel={onHandleCancel}
              onClick={onHandleClick}
              aria-expanded={menu ? undefined : expanded}
              aria-label={menu ? t("uiDialogCloseMenu", "Close Menu") : expanded ? t("uiDialogShrinkPanel", "Shrink Panel to Fit Its Content") : t("uiDialogExpandPanel", "Expand Panel to Full Height")}
              // `touch-none` hands the gesture to us instead of the browser
              // trying to scroll with it. The bar itself is 4px tall and nobody
              // aims at 4px, so the button pads out to a real target while
              // `-mb-2` keeps it from pushing the content down. `w-40
              // self-center`: the handle is a handle, not the whole top edge —
              // stretched across the sheet it sat under the close button and
              // toggled the height from anywhere along the header strip.
              className="hit-target flex shrink-0 -mb-2 cursor-grab touch-none justify-center self-center w-40 pb-3 pt-4 outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded active:cursor-grabbing"
            >
              <span data-sheet-handle className="block h-1 w-9 rounded-hair" />
            </button>
            {/* The gesture needs something to click to close the dialog, and
                this component cannot reach the Root's setter. Out of the tab
                order and unannounced: everyone who cannot make the gesture
                already has Escape, the scrim and the X. */}
            <DialogPrimitive.Close ref={dismiss} tabIndex={-1} aria-hidden="true" className="hidden" />
          </>
        )}
        {children}
        {/* AFTER the children, deliberately (2026-09-27). The header draws a
            bar edge and so stacks (`relative z-raised`); rendered before it,
            this ✕ sat UNDER the header at the same z-index and every tap on it
            landed on the header instead — "a lot of the close buttons on the
            modals are not working". Last in the DOM, it is on top of anything
            at its rung.

            focus-visible, not focus: with plain `focus:` the ring appeared on a
            mouse click too, which reads as a stuck state on the one control
            every dialog has. */}
        {!hideClose && (
          <DialogPrimitive.Close
            // Inert while `locked`: disabled, so it cannot be pressed and says so.
            disabled={locked}
            className={cn(CLOSE_BUTTON_CLASS, CLOSE_CORNER_CLASS)}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">{t("close", "Close")}</span>

          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

/*
  A SHORT window: an iPhone in landscape (874×402), a split-screen iPad.

  The centred card normally pins its header and footer and scrolls only the
  body between them. At 402px tall a dialog with a real header — the privacy
  policy's eyebrow, title, summary and dates — kept ~160px of header and ~70px
  of footer pinned and left 116px of text to read through. So when the window
  is at most 500px tall, the CARD becomes the scroller: the header scrolls
  away with the text, the body takes its natural height, and only the footer
  (the actions) stays pinned, at the bottom.

  Scoped three ways so nothing changes anywhere else:
  - the media query — every normal-height window is untouched;
  - `data-dialog-shape="card"` — the phone sheet keeps its own scroller, its
    drag handle and its gesture;
  - `[data-dialog-shape=card]>&` — only a DialogBody / DialogFooter that is a
    DIRECT child of the card changes. One wrapped in a <form> keeps the
    pinned layout: a sticky footer inside a height-limited form would ride up
    with the form instead of staying put.

  The card is `overflow-clip` at every other height; it only becomes a
  scroll container here, where it has something to scroll.
*/
/*
  A DIALOG WITH TABS HOLDS ONE HEIGHT (Andrew, 2026-11-03: "dialogs should
  never shift when switching tabs even if the tab is larger than the other
  vertically. Always fix the dialog vertically. Make this a app rule on
  desktop/tablet").

  The card is centred and sized to its content, so a tab taller than the last
  grew it both ways: the header and the tabs themselves jumped up, the footer
  jumped down, and the tab the reader had just pressed moved out from under
  the pointer. Now a card holding a tab strip — Radix Tabs, SegmentedTabs,
  the wizard's steps, anything with role="tablist" — takes a FIXED height:
  the whole available window (the same cap as `max-h` above) up to 56rem,
  whichever is less. A short tab leaves room at the bottom of the body; a
  long one scrolls inside it. DialogBody is `flex-1`, so the footer stays on
  the card's bottom edge either way.

  Automatic on purpose, so a new tabbed dialog cannot forget it. The card
  shape only (sm and up): the phone sheet has its own two heights and its
  own gesture. A dialog whose "tabs" are a small choice that changes nothing
  below them opts out with data-dialog-height="content"; a dialog that wants
  a different fixed size sets --dialog-fixed-h. Pinned by
  tests/dialogTabHeight.test.ts.
*/
const TABBED_CARD_HEIGHT =
  "[&:has([role=tablist]):not([data-dialog-height=content])]:h-[min(calc(var(--vv-h,100dvh)_-_2rem_-_var(--sa-top)_-_var(--sa-bottom)),var(--dialog-fixed-h,56rem))]"

// Spelled out in full, not built from a prefix: Tailwind finds classes by
// reading the source, and a class assembled in a template literal is never
// generated.
const SHORT_CARD_CONTENT =
  "[@media(max-height:500px)]:overflow-y-auto [@media(max-height:500px)]:overflow-x-hidden [@media(max-height:500px)]:overscroll-contain"
const SHORT_BODY =
  "[@media(max-height:500px)]:[[data-dialog-shape=card]>&]:flex-none [@media(max-height:500px)]:[[data-dialog-shape=card]>&]:overflow-visible"
const SHORT_FOOTER =
  "[@media(max-height:500px)]:[[data-dialog-shape=card]>&]:sticky [@media(max-height:500px)]:[[data-dialog-shape=card]>&]:bottom-0 [@media(max-height:500px)]:[[data-dialog-shape=card]>&]:z-raised [@media(max-height:500px)]:[[data-dialog-shape=card]>&]:bg-[hsl(var(--card-bottom))]"

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // Left-aligned at every width, PropPay's way (B5, 2026-09-28). A centred
      // title on the phone sheet sat over left-aligned fields and a
      // left-aligned footer — the one line on the panel on a different axis.
      "flex flex-col gap-1.5 text-left",
      // min-w-0 so a long title inside this column can shrink and truncate
      // instead of forcing the header wider than the panel.
      "shrink-0 min-w-0 px-card pt-card pb-3",
      // PropPay's bar edge — a hairline and a soft shadow, so the body reads as
      // scrolling UNDER the header — but only once it HAS scrolled (Andrew,
      // 2026-11-03, "premium": at rest the band read as heavy chrome over a form
      // nobody had touched). The body marks itself `data-scrolled` and
      // `.dialog-head` in index.css draws the edge from that. A sr-only header
      // draws none.
      "dialog-head relative z-raised [&.sr-only]:shadow-none [&.sr-only]:border-0",
      // …and must stay out of the layout. `.relative` and the padding are
      // emitted after `.sr-only`, so they won: the header became a 1px box
      // with 20px of padding and no clip (clip only applies to an absolute
      // box), and the first letters of a hidden title showed through — the
      // "Contac" peeking over the phone overflow sheet. These re-assert it.
      "[&.sr-only]:absolute [&.sr-only]:p-0",
      // Reserve the close button's box. It is absolutely positioned at
      // right-3 and is 44px wide (min-w-touch), so without this a long title
      // simply runs underneath it. `pe-*` is logical, so this still works if
      // the app is ever rendered RTL.
      "pe-14",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/** Marks a dialog body `data-scrolled` once it has moved, for the header's edge. Set on the node, no re-render. */
function markScrolled(e: React.UIEvent<HTMLDivElement>) {
  const el = e.currentTarget
  const scrolled = el.scrollTop > 0
  if ((el.dataset.scrolled === "") !== scrolled) {
    if (scrolled) el.dataset.scrolled = ""
    else delete el.dataset.scrolled
  }
}

const DialogBody = ({
  className,
  onScroll,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    onScroll={(e) => {
      markScrolled(e)
      onScroll?.(e)
    }}
    // A scroll region a keyboard can reach (axe: scrollable-region-focusable).
    // The body of a long read-only dialog — the privacy policy — had no
    // focusable content, so it could not be scrolled without a pointer.
    tabIndex={0}
    // Marks the scroll region so DialogContent never lands on it when the
    // dialog opens (see `firstTypedField`) — only a reader's own Tab does.
    data-dialog-body=""
    className={cn(
      // overflow-x hidden + pan-y: a body that scrolls vertically must not
      // also pan sideways. `overflow-y: auto` alone makes overflow-x auto as
      // well, and on iOS one child a pixel too wide (the account sheet's email
      // field) let the whole sheet be dragged left and right while scrolling.
      "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden [touch-action:pan-y] px-card py-3",
      // Momentum + contained scroll chaining. Without `overscroll-behavior`,
      // flicking past the end of a dialog body scrolls the page behind it — the
      // single most common reason a web view feels wrong on iOS.
      "scroll-pane focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      SHORT_BODY,
      className
    )}
    {...props}
  />
)
DialogBody.displayName = "DialogBody"

/** The footer's chrome: its inset and the hairline above it. */
const FOOTER_CHROME = `shrink-0 px-card pb-card pt-3 border-t border-border/60 ${SHORT_FOOTER}`

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * A secondary action set APART from the pair that closes the dialog — "Send
   * test to me", "Delete" on an edit form. On a desk it sits at the far left;
   * on a phone it takes a full-width row of its own above the closing pair,
   * rather than making three buttons share one row (B3).
   */
  start?: React.ReactNode
}

const DialogFooter = ({ className, start, children, ...props }: DialogFooterProps) => {
  if (!start) {
    return (
      <div className={cn(dialogActionRow, FOOTER_CHROME, className)} {...props}>
        {children}
      </div>
    )
  }
  return (
    <div
      className={cn("flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center", FOOTER_CHROME, className)}
      {...props}
    >
      <div className="flex flex-wrap gap-2 [&>*]:flex-1 sm:mr-auto sm:[&>*]:flex-none">{start}</div>
      <div className={cn(dialogActionRow, "sm:ml-auto")}>{children}</div>
    </div>
  )
}
DialogFooter.displayName = "DialogFooter"

// Reusable layout helper for dialog sections with responsive columns.
// `md:` is the iPad-portrait step and it is load-bearing — a grid that jumps
// straight from one column to `lg:` leaves 768–1023px rendering phone-width
// content across a tablet's canvas.
const DialogSection = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "grid grid-cols-1 gap-stack md:grid-cols-2",
      className
    )}
    {...props}
  />
)
DialogSection.displayName = "DialogSection"

/**
 * A titled group of fields inside a dialog. NOT a Card: a card inside a
 * sheet is a bordered, shadowed box inside a bordered, shadowed box, and on
 * a phone the Company and Project dialogs stacked three of them — three
 * nested edges between the title and the first field. iOS groups a form
 * with a heading and a hairline, so this does too: the first group sits
 * flush under the header, every later one starts with a rule.
 */
/*
  A group after the first draws its divider as a top border, with room on
  BOTH sides of the line. It used to have padding below and nothing above,
  leaning on the parent to space its children; where the parent didn't (the
  Availability Filters sheet, 2026-11-03: the line sat flush under the unit
  count and under the selects), the rule looked misplaced. The top margin is
  the group's own, and between block siblings it collapses with any spacing
  a `space-y-*` parent already gives, so nowhere gets a double gap.
*/
const DialogGroup = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <section
    className={cn("space-y-4 [&:not(:first-child)]:mt-5 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-border [&:not(:first-child)]:pt-5", className)}
    {...props}
  />
)
DialogGroup.displayName = "DialogGroup"

const DialogGroupHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("space-y-1", className)} {...props} />
)
DialogGroupHeader.displayName = "DialogGroupHeader"

const DialogGroupTitle = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn("text-body font-semibold leading-snug", className)} {...props} />
)
DialogGroupTitle.displayName = "DialogGroupTitle"

const DialogGroupDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn("text-label text-muted-foreground", className)} {...props} />
)
DialogGroupDescription.displayName = "DialogGroupDescription"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-body-lg font-bold leading-snug", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-body text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogSection,
  DialogGroup,
  DialogGroupHeader,
  DialogGroupTitle,
  DialogGroupDescription,
  DialogTitle,
  DialogDescription,
}
